import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { SetRoutingDestinationInput, SetRoutingDestinationOutput } from 'src/models/setRoutingDestination';
import RIVBFFService from 'src/services/riv-bff-service';
import * as ErrorConstants from 'src/localization/string-ids/error-constants';
import { resetIsLoading, resetUiAlertState, setIsLoading, setUiAlertState } from 'src/store/common-slices/uiSlice';
import { AlertLevels } from 'src/models/alert-model';
import * as SuccessConstants from 'src/localization/string-ids/success-constants';
import { RIVApiMethods } from 'src/constants/api-method-constants';

export interface setRoutingDestinationState {
    routingDestinationSetSuccessfully: boolean;
}

const initialState: setRoutingDestinationState = {
    routingDestinationSetSuccessfully: false
}

export const setRoutingDestination = createAsyncThunk<SetRoutingDestinationOutput, SetRoutingDestinationInput>(
    'setRoutingDestination/setRoutingDestination',
    async (setRoutingDestinationInput: SetRoutingDestinationInput, {dispatch, rejectWithValue}) => {
        dispatch(resetUiAlertState());
        dispatch(setIsLoading());

        try {
            const setRoutingDestinationOutput = await RIVBFFService.triggerSetRoutingDestinationAPI(setRoutingDestinationInput);
            dispatch(resetIsLoading());
            dispatch(setUiAlertState({
                alertLevel: AlertLevels.SUCCESS,
                alertTitleString: `${setRoutingDestinationInput.containerId}`,
                alertMessageString: SuccessConstants.SET_ROUTING_DESTINATION_SUCCESS,
            }));
            return setRoutingDestinationOutput;

        } catch (error) {
            dispatch(resetIsLoading());

            if (error && typeof error === 'object' && 'errorMessage' in error && 'errorStatus' in error) {
                const apiError = error as { errorMessage: string; errorStatus: number };

                dispatch(setUiAlertState({
                    alertLevel: AlertLevels.ERROR,
                    alertTitleString: `${setRoutingDestinationInput.containerId}`,
                    alertMessageString: apiError.errorMessage,
                    alertStatus: apiError.errorStatus,
                    UIAction: RIVApiMethods.SET_ROUTING_DESTINATION,
                    warehouse: setRoutingDestinationInput.warehouseId,
                    associate: setRoutingDestinationInput.associate,
                }));
                return rejectWithValue({
                    data: apiError.errorMessage,
                    status: apiError.errorStatus
                });
            }
            else if (error instanceof Error) {
                dispatch(setUiAlertState({
                    alertLevel: AlertLevels.ERROR,
                    alertTitleString: `${setRoutingDestinationInput.containerId}`,
                    alertMessageString: error.message
                }));
                return rejectWithValue(error.message);
            }
            else {
                dispatch(setUiAlertState({
                    alertLevel: AlertLevels.ERROR,
                    alertTitleString: `${setRoutingDestinationInput.containerId}`,
                    alertMessageString: ErrorConstants.UNKNOWN_ERROR
                }));
                return rejectWithValue(ErrorConstants.UNKNOWN_ERROR);
            }
        }
    }
);

const setRoutingDestinationSlice = createSlice({
    name: 'setRoutingDestination',
    initialState,
    reducers: {
        resetSetRoutingDestinationFlag: (state) => {
            state.routingDestinationSetSuccessfully = false;
        }
    },
    extraReducers: builder => {
        builder
            .addCase(setRoutingDestination.pending, (state) => {
                state.routingDestinationSetSuccessfully = false;
            })
            .addCase(setRoutingDestination.fulfilled, (state) => {
                state.routingDestinationSetSuccessfully = true;
            })
            .addCase(setRoutingDestination.rejected, (state) => {
                state.routingDestinationSetSuccessfully = false;
            })
    }
});

export const { resetSetRoutingDestinationFlag } = setRoutingDestinationSlice.actions;
export default setRoutingDestinationSlice.reducer;
